/**
 * Client-side preparation of the merchant's photo, before it leaves the
 * browser: validate the file, downscale on a canvas, re-encode as JPEG.
 *
 * Same constants and approach as the storefront widget's imageUtils.ts —
 * proven on real shopper traffic — but the admin cannot import across the
 * extension package boundary, so the small pieces it needs live here.
 * Re-encoding to JPEG also quietly solves format drift: the engine accepts
 * only JPEG/PNG, while merchants will hand us WebP and HEIC-exported files.
 */

export const MAX_PHOTO_SOURCE_BYTES = 20 * 1024 * 1024;
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
export const MAX_PHOTO_WIDTH = 768;
export const MAX_PHOTO_HEIGHT = 1024;
export const JPEG_QUALITY = 0.85;

const VALID_INPUT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export type PhotoValidationError = 'invalid_type' | 'too_large';

/** Screen a picked file before reading a byte of it. */
export function validatePhotoFile(file: File): PhotoValidationError | null {
  if (!VALID_INPUT_TYPES.includes(file.type)) return 'invalid_type';
  if (file.size > MAX_PHOTO_SOURCE_BYTES) return 'too_large';
  return null;
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result);
      else reject(new Error('Failed to read file'));
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}

/**
 * Read, downscale and re-encode a photo file.
 *
 * @returns A JPEG data URL bounded to the widget's proven avatar dimensions
 */
export async function preparePhoto(file: File): Promise<string> {
  const dataUrl = await readAsDataUrl(file);
  const img = await loadImage(dataUrl);

  let { width, height } = img;
  if (width > MAX_PHOTO_WIDTH || height > MAX_PHOTO_HEIGHT) {
    const ratio = Math.min(MAX_PHOTO_WIDTH / width, MAX_PHOTO_HEIGHT / height);
    width = Math.floor(width * ratio);
    height = Math.floor(height * ratio);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
}

/** Turn the prepared data URL back into the Blob the upload sends. */
export function dataUrlToBlob(dataUrl: string): Blob {
  const [, base64] = dataUrl.split(',');
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: 'image/jpeg' });
}
